import { acceptHMRUpdate, defineStore } from 'pinia'
import type { TWorkday } from './EventStore'
import { now } from './EventStore'
import type { TDehydratedEvent } from '@/classes/Event'
import { Event } from '@/classes/Event'
import { CorrectionService, TravelLogService } from '@/api/client'
import { DateTime } from 'luxon'
import handleRequest from '@/helpers/handleRequest'
import type { TDehydratedTravelLog } from '@/classes/sendables/TravelLog'
import { TravelLog } from '@/classes/sendables/TravelLog'
import { useEmployeeStore } from './EmployeeStore'
import { useConfigStore } from './ConfigStore'

type tState = {
  workdays: Array<TWorkday>
  lastUpdate: DateTime | null
  travelLogs: TravelLog[]
}

const initialState: tState = {
  workdays: [],
  lastUpdate: null,
  travelLogs: [],
}

const useCorrectionStore = defineStore('correctionStore', {
  state: () => {
    return { ...initialState }
  },
  getters: {
    workday: (state) => {
      return (date: string): TWorkday | undefined => {
        if (!state.workdays) {
          return undefined
        }
        return state.workdays.find((workday) => workday.date === date)
      }
    },
    noData: (state) => {
      return state.lastUpdate === null
    },
    lastEvent: (state): Event | undefined => {
      if (state.workdays.length > 0) {
        const lastWorkday = state.workdays[state.workdays.length - 1]
        if (lastWorkday.events.length > 0) {
          return lastWorkday.events[lastWorkday.events.length - 1]
        }
      }
    },

    lastWorkday: (state): TWorkday | undefined => {
      if (state.workdays.length > 0) {
        return state.workdays[state.workdays.length - 1]
      }
    },

    lastEventWorkSeconds(): number {
      // if the last Event is not a work event it will return a Duration of 0
      const lastEvent = this.lastEvent
      return lastEvent && lastEvent.isWorkEvent()
        ? Math.abs(lastEvent.occured_at.diff(now.value).as('seconds'))
        : 0
    },
  },
  actions: {
    getTravelLogByEventId(event_id: number): TravelLog | undefined {
      return this.travelLogs.find(
        (t) => t.travel_log_data.event_id === event_id,
      )
    },
    getEventById(event_id: number): Event | undefined {
      for (const workday of this.workdays) {
        const event = workday.events.find((e) => e.id === event_id)
        if (event) {
          return event
        }
      }
    },
    async sync() {
      const data = (
        await handleRequest(CorrectionService.getWorkdaysForCorrection())
      )?.data
      console.log('CorrectionStore pulled data from server', data)
      if (!data) return
      const eventIds = data
        .flatMap((workday) => workday.events)
        .map((e) => e.id)
        .filter((id) => id !== undefined)
      const employeeId = useEmployeeStore().employee?.id
      let travelLogData
      let travelLogs
      if (
        useConfigStore().travel_log_enabled &&
        employeeId &&
        eventIds &&
        eventIds.length > 0
      ) {
        travelLogData = (
          await handleRequest(
            TravelLogService.getTravelLogs({
              event_ids: eventIds,
              employee_id: employeeId,
            }),
          )
        )?.data

        travelLogs = travelLogData?.map((t) =>
          TravelLog.fromServerObject(t, employeeId),
        )
      }

      const newState: tState = {
        workdays: [],
        lastUpdate: DateTime.now(),
        travelLogs: travelLogs ?? [],
      }
      for (const workday of data) {
        const events = workday.events.map((event) =>
          Event.fromServerObject(event),
        )

        newState.workdays.push({
          date: workday.date ?? '',
          events: events,
          work_seconds: workday.work_seconds,
          break_seconds: workday.break_seconds,
          target_seconds: workday.target_seconds ?? 0,
          absence_seconds: workday.absence_seconds,
          absence_categories: workday.absence_categories,
          starts_at: workday.starts_at as string,
          ends_at: workday.ends_at ?? null,
          employee_id: workday.employee_id ?? 0,
        })
      }
      this.$state = newState
    },
    hydrate(state: string) {
      this.$state = JSON.parse(state, (key, value) => {
        if (key === 'events') {
          return value.map((eventString: TDehydratedEvent) =>
            Event.fromJSON(eventString),
          )
        } else if (key === 'travelLogs') {
          return value.map((travelLogString: TDehydratedTravelLog) =>
            TravelLog.fromJSON(travelLogString),
          )
        } else if (key === 'lastUpdate') {
          return DateTime.fromISO(value)
        } else {
          return value
        }
      })
    },
  },
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useCorrectionStore, import.meta.hot))
}

export { useCorrectionStore }
export type { TWorkday }
